import type { HttpErrorResponse } from '@angular/common/http';

import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/shared/services/alert.service';
import {
  ApplicationSearchParams,
  CommonGraphqlService,
} from 'src/app/shared/services/common.graphql.service';

import { IApplicationListItem } from './../../../interfaces/account/account.interface';

@Component({
  selector: 'app-add-company-to-user-modal',
  templateUrl: './add-company-to-user-modal.component.html',
})
export class AddCompanyToUserModalComponent implements OnInit, OnDestroy {
  applications?: IApplicationListItem[];
  loadDataOnModalOpen: boolean;

  vendorFiltersForm: UntypedFormGroup;
  applicationsDTO?: IApplicationDTO[];

  isLoading = false;

  isAllGroupsChecked?: boolean;

  checkedApplications: IApplicationListItem[] = [];

  nextPageCursor?: string;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private commonGraphqlService: CommonGraphqlService,
    private activeModal: NgbActiveModal,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.vendorFiltersForm = this.fb.group({
      name: [''],
      taxId: [''],
      clientId: [''],
      groupId: [''],
    });

    this.applicationsDTO = [];
    this.checkedApplications = this.applications ? [...this.applications] : [];

    if (this.loadDataOnModalOpen) {
      this.getCompanies();
    }
  }

  doSearch() {
    const params = this.vendorFiltersForm.value as ApplicationSearchParams;
    this.applicationsDTO = undefined;
    this.isLoading = true;

    this.getCompanies(params);
  }

  toggleAllVendors(event?) {
    if (event) {
      this.isAllGroupsChecked = event.target.checked;
    }
    const condition = this.isAllGroupsChecked ?? !this.isAllGroupsChecked;

    if (condition) {
      const toggledApplications = this.applicationsDTO?.map((x) => {
        if (this.isAllGroupsChecked) {
          x.checked = this.isAllGroupsChecked;
        }
        return x;
      });

      if (toggledApplications) {
        for (const toggledApp of toggledApplications) {
          const checkedApp = this.checkedApplications.find((x) => {
            return x.code == toggledApp.code;
          });

          if (this.isAllGroupsChecked && !checkedApp) {
            this.checkedApplications.push(toggledApp);
          } else if (!this.isAllGroupsChecked && checkedApp) {
            this.checkedApplications = this.checkedApplications.filter((x) => {
              return x.code !== checkedApp.code;
            });
          }
        }
      }
    }
  }

  onCheckboxChange(value: IApplicationDTO) {
    this.checkedApplications = this.checkedApplications.some((x) => {
      return x.code === value.code;
    })
      ? this.checkedApplications.filter((x) => {
          return x.code !== value.code;
        })
      : [...this.checkedApplications, value];
  }

  addVendors() {
    this.activeModal.close(this.checkedApplications);
  }

  close() {
    this.activeModal.dismiss();
  }

  onScroll() {
    if (this.nextPageCursor) {
      const params: ApplicationSearchParams = {
        ...this.vendorFiltersForm.value,
        cursor: this.nextPageCursor,
      };
      this.getCompanies(params);
    }
  }

  private getCompanies(params?: ApplicationSearchParams) {
    this.isLoading = true;
    this.subscriptions.add(
      this.commonGraphqlService.getApplicationsForUser(params).subscribe({
        next: (response) => {
          const page = response;
          const apps = page.nodes;

          this.nextPageCursor = page.pageInfo.hasNextPage
            ? page.pageInfo.endCursor
            : undefined;

          const formatedApps = apps.map((x) => {
            return {
              ...x,
              checked:
                this.applications?.some((a) => {
                  return a.code == x.code;
                }) ?? false,
            };
          });

          this.applicationsDTO = this.applicationsDTO ?? [];
          if (formatedApps.length > 0) {
            this.applicationsDTO.push(...formatedApps);
          }
          const everyApplicationIsChecked = this.applicationsDTO.every((x) => {
            return x.checked;
          });
          this.isAllGroupsChecked = everyApplicationIsChecked;
          this.isLoading = false;
        },
        error: (error: unknown) => {
          this.isLoading = false;
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }
}

export type IApplicationDTO = {
  checked: boolean;
} & IApplicationListItem;
